import { ArrowLeft, Pencil, Receipt, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsFacture, useBudgetLignes, useFacture } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader, PaiementBadge, StatutBadge } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { HistoriqueListe, InstructionsTable, StatutFactureBadge } from '@/components/courrier';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, montant } from '@/lib/utils';

export function FactureDetail() {
  const { id } = useParams();
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useFacture(id);
  const actions = useActionsFacture();
  const [dialogue, setDialogue] = useState<'soumettre' | 'decision' | 'comptabiliser' | 'retourner' | 'cloturer' | null>(null);
  const [decision, setDecision] = useState<{ action: 'ORIENTER' | 'SUSPENDRE'; annotation: string; motif: string }>({ action: 'ORIENTER', annotation: 'Bon pour comptabilisation et paiement.', motif: '' });
  const [compta, setCompta] = useState({ ligneBudgetaireId: '', commentaire: '' });
  const [toutesLignes, setToutesLignes] = useState(true);
  const budget = useBudgetLignes(toutesLignes ? undefined : u.structureCode);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { facture: f, actions: a, delai } = data;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/factures" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Factures reçues
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {f.numero} <StatutFactureBadge statut={f.statut} />
          </span>
        }
        sousTitre={
          <>
            {f.fournisseur} · facture {f.numeroFacture} du {dateFr(f.dateFacture)} · reçue le {dateFr(f.dateReception)} · <span className="num font-medium text-foreground">{montant(f.montantUsd, true)}</span>
          </>
        }
        actions={
          <>
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/factures/${f.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre au DG
              </Button>
            )}
            {a.decider && (
              <Button onClick={() => { setDecision({ action: 'ORIENTER', annotation: f.annotation ?? 'Bon pour comptabilisation et paiement.', motif: '' }); setDialogue('decision'); }}>
                Décider
              </Button>
            )}
            {a.comptabiliser && (
              <Button onClick={() => { setCompta({ ligneBudgetaireId: f.ligneBudgetaireId ?? f.bonCommande?.ligneBudgetaireId ?? '', commentaire: '' }); setDialogue('comptabiliser'); }}>
                Comptabiliser
              </Button>
            )}
            {a.engager && (
              <Button asChild>
                <Link to={`/fed/nouveau?facture=${f.id}`}>
                  <Receipt className="size-4" /> Émettre le FED
                </Link>
              </Button>
            )}
            {a.retourner && (
              <Button variant="outline" onClick={() => setDialogue('retourner')}>
                Retourner
              </Button>
            )}
            {a.cloturer && (
              <Button variant="outline" onClick={() => setDialogue('cloturer')}>
                Clôturer
              </Button>
            )}
          </>
        }
      />

      {f.statut === 'SUSPENDUE' && (
        <Alert variant="destructive">
          <AlertTitle>Suspendue par le Directeur Général</AlertTitle>
          <AlertDescription>{f.motif}</AlertDescription>
        </Alert>
      )}
      {f.statut === 'RETOURNEE' && (
        <Alert>
          <AlertTitle>Retournée à l'assistante</AlertTitle>
          <AlertDescription>{f.motif}</AlertDescription>
        </Alert>
      )}
      {!['PAYEE', 'CLOTUREE', 'ANNULEE'].includes(f.statut) && (delai.depasse || delai.proche) && (
        <Alert variant={delai.depasse ? 'destructive' : 'default'}>
          <AlertTitle>{delai.depasse ? 'Délai de paiement dépassé' : 'Délai de paiement proche'}</AlertTitle>
          <AlertDescription>
            {delai.jours} jour(s) depuis la réception, pour un objectif de {delai.cible} jours.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Facture</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="sm:col-span-2"><span className="text-muted-foreground">Objet</span><div>{f.objet}</div></div>
              <div>
                <span className="text-muted-foreground">Montant TTC</span>
                <div className="num">
                  {f.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {f.devise}
                  {f.devise !== 'USD' && <span className="text-muted-foreground"> · {montant(f.montantUsd, true)}</span>}
                </div>
              </div>
              <div><span className="text-muted-foreground">dont TVA</span><div className="num">{f.tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {f.devise}</div></div>
              <div><span className="text-muted-foreground">Référence contrat / marché</span><div>{f.referenceCommande ?? '—'}</div></div>
              <div className="sm:col-span-2"><span className="text-muted-foreground">Enregistrée par</span><div>{f.enregistrePar ? `${f.enregistrePar.prenom} ${f.enregistrePar.nom}` : '—'} · {dateHeureFr(f.createdAt)}</div></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Rapprochement avec la commande</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {f.bonCommande ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <span className="text-muted-foreground">Bon de commande</span>
                    <div>
                      <Link to={`/achats/${f.bonCommande.id}`} className="font-medium text-primary hover:underline">
                        {f.bonCommande.numero ?? 'BC'}
                      </Link>
                    </div>
                  </div>
                  <div><span className="text-muted-foreground">Montant commandé</span><div className="num">{montant(f.bonCommande.montantUsd, true)}</div></div>
                  <div>
                    <span className="text-muted-foreground">Écart facture − commande</span>
                    <div className={cn('num', (f.ecartCommande ?? 0) > 0 && 'font-semibold text-red-700')}>{montant(f.ecartCommande ?? 0, true)}</div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Aucun bon de commande rattaché{f.referenceCommande ? ` (référence déclarée : ${f.referenceCommande})` : ''}.</p>
              )}
            </CardContent>
          </Card>

          {f.annotation && f.statut !== 'SUSPENDUE' && (
            <Card>
              <CardHeader>
                <CardTitle>Annotation du Directeur Général</CardTitle>
                <CardDescription>{dateHeureFr(f.dateOrientation)}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line text-sm">{f.annotation}</p>
              </CardContent>
            </Card>
          )}

          {(f.ligneBudgetaire || data.fed) && (
            <Card>
              <CardHeader>
                <CardTitle>Comptabilisation et engagement</CardTitle>
                {f.dateComptabilisation && <CardDescription>Comptabilisée le {dateHeureFr(f.dateComptabilisation)}</CardDescription>}
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Imputation budgétaire</span>
                  <div>{f.ligneBudgetaire ? `${f.ligneBudgetaire.code} · ${f.ligneBudgetaire.libelle}` : '—'}</div>
                </div>
                <div><span className="text-muted-foreground">Commentaire du Chef comptable</span><div>{f.commentaireComptable ?? '—'}</div></div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Fiche d'engagement (FED)</span>
                  {data.fed ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/fed/${data.fed.id}`} className="font-medium text-primary hover:underline">
                        {data.fed.numero ?? 'FED (brouillon)'}
                      </Link>
                      <StatutBadge statut={data.fed.statut} />
                      <PaiementBadge etat={data.fed.etatPaiement} />
                      <span className="num">{montant(data.fed.montantUsd, true)}</span>
                      {f.datePaiement && <span className="text-muted-foreground">payée le {dateFr(f.datePaiement)}</span>}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">Pas encore émise.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Instructions ({data.instructions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <InstructionsTable instructions={data.instructions} document={false} vide="Aucune instruction pour cette facture." />
            </CardContent>
          </Card>
          <PiecesJointes objetType="FA" objetId={f.id} typesSuggeres={['FACTURE', 'BON_COMMANDE', 'CONTRAT', 'JUSTIFICATIF', 'AUTRE']} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoriqueListe historique={f.historique} />
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmationDialog
        ouvert={dialogue === 'soumettre'}
        onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)}
        titre="Soumettre au Directeur Général"
        description="La facture entre dans le parapheur du DG pour orientation vers la Finance (règle R4 : fournisseur, numéro, dates, objet et montant sont obligatoires)."
        libelle="Soumettre"
        onConfirmer={() => executer(() => actions.soumettre.mutateAsync(f.id), 'Facture soumise au DG.')}
        chargement={actions.soumettre.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'retourner'}
        onOpenChange={(o) => setDialogue(o ? 'retourner' : null)}
        titre="Retourner la facture"
        description="La facture revient à l'assistante pour correction ou complément ; le motif est tracé."
        libelle="Retourner"
        onConfirmer={(m) => (m.length < 5 ? Promise.resolve(toast.error('Le motif est obligatoire.')) : executer(() => actions.retourner.mutateAsync({ id: f.id, motif: m }), 'Facture retournée.'))}
        chargement={actions.retourner.isPending}
        champ={{ label: 'Motif (obligatoire)' }}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'cloturer'}
        onOpenChange={(o) => setDialogue(o ? 'cloturer' : null)}
        titre="Clôturer la facture"
        description="Clôture du dossier après paiement, ou classement d'une facture suspendue ou retournée."
        libelle="Clôturer"
        onConfirmer={() => executer(() => actions.cloturer.mutateAsync(f.id), 'Facture clôturée.')}
        chargement={actions.cloturer.isPending}
      />

      <Dialog open={dialogue === 'decision'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Décision du Directeur Général · {f.numero}</DialogTitle>
            <DialogDescription>L'orientation crée une instruction pour action au Chef comptable et une information au Directeur financier.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={decision.action === 'ORIENTER' ? 'default' : 'outline'} onClick={() => setDecision({ ...decision, action: 'ORIENTER' })}>
                Orienter vers la Finance
              </Button>
              <Button type="button" size="sm" variant={decision.action === 'SUSPENDRE' ? 'destructive' : 'outline'} onClick={() => setDecision({ ...decision, action: 'SUSPENDRE' })}>
                Suspendre
              </Button>
            </div>
            {decision.action === 'ORIENTER' ? (
              <div className="space-y-1.5">
                <Label>Annotation</Label>
                <Textarea rows={3} value={decision.annotation} onChange={(e) => setDecision({ ...decision, annotation: e.target.value })} />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Motif de la suspension (obligatoire)</Label>
                <Textarea rows={3} value={decision.motif} onChange={(e) => setDecision({ ...decision, motif: e.target.value })} placeholder="Prestation non réceptionnée, montant contesté…" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button
              variant={decision.action === 'SUSPENDRE' ? 'destructive' : 'default'}
              disabled={actions.decider.isPending || (decision.action === 'SUSPENDRE' && !decision.motif.trim())}
              onClick={() => executer(() => actions.decider.mutateAsync({ id: f.id, action: decision.action, annotation: decision.annotation.trim() || undefined, motif: decision.motif.trim() || undefined }), decision.action === 'ORIENTER' ? 'Facture orientée vers la Finance.' : 'Facture suspendue.')}
            >
              {decision.action === 'ORIENTER' ? 'Orienter' : 'Suspendre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogue === 'comptabiliser'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Comptabiliser la facture {f.numero}</DialogTitle>
            <DialogDescription>Vérification, imputation budgétaire et confirmation ; la facture est transmise automatiquement à la CPM pour émission du FED.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Ligne budgétaire d'imputation</Label>
              <SelectNatif value={compta.ligneBudgetaireId} onChange={(e) => setCompta({ ...compta, ligneBudgetaireId: e.target.value })}>
                <option value="">Choisir une ligne</option>
                {(budget.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} · {l.libelle} · disponible {montant(l.disponiblePrevisionnel)}
                  </option>
                ))}
              </SelectNatif>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={toutesLignes} onCheckedChange={(c) => setToutesLignes(c === true)} />
                Afficher toutes les lignes du Fonds
              </label>
            </div>
            <div className="space-y-1.5">
              <Label>Commentaire (écriture, contrôles effectués)</Label>
              <Textarea rows={2} value={compta.commentaire} onChange={(e) => setCompta({ ...compta, commentaire: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button disabled={!compta.ligneBudgetaireId || actions.comptabiliser.isPending} onClick={() => executer(() => actions.comptabiliser.mutateAsync({ id: f.id, ligneBudgetaireId: compta.ligneBudgetaireId, commentaire: compta.commentaire.trim() || undefined }), 'Facture comptabilisée et transmise à la CPM.')}>
              Confirmer la comptabilisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
